import { T } from "./Translate";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { db, auth, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { safeHttpUrl, openSafeUrl } from "../utils/safeUrl";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  increment,
} from "firebase/firestore";
import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Avatar,
  AppBar,
  IconButton,
  Toolbar,
  Paper,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "../icons";
import { Send as SendIcon } from "../icons";
import { Person as PersonIcon } from "../icons";
import { CameraAlt as CameraAltIcon } from "../icons";
import { AttachFile as AttachFileIcon } from "../icons";
import { EmojiEmotions as EmojiEmotionsIcon } from "../icons";
import { Close as CloseIcon } from "../icons";
import { brandColors, barColors } from "../theme";
import { notifyUser } from "../utils/notifications";

const DESKTOP_CONTENT_MAX_WIDTH = 980;

// Emojis populares organizados por categorías
const EMOJI_CATEGORIES = {
  "Frecuentes": ["😊", "😂", "❤️", "👍", "🙏", "😍", "🔥", "💪"],
  "Emociones": ["😀", "😃", "😄", "😁", "😅", "🤣", "😇", "😉", "😌", "🥰", "😘", "😋", "😎", "🤩", "😏", "😞", "😔", "😟", "😢", "😭", "😤", "😠"],
  "Gestos": ["👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "👏", "🙌", "👐", "🤲", "🙏", "💪"],
  "Corazones": ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "💔", "💕", "💞", "💓"],
  "Objetos": ["🎉", "🎊", "🎈", "🎁", "⭐", "✨", "🔥", "💧", "🌈", "☀️", "🌙"],
};

// Chats por publicación: /chatbox/auto/:itemId/:otherUserId y
// /chatbox/viaje/:itemId/:otherUserId usan esta misma pantalla que el chat
// directo, con la publicación arriba. Antes tenían su propia pantalla
// (chatbox.js) con otro diseño, sin adjuntos y que fallaba al enviar si la
// cuenta no tenía nombre.
const ITEM_COLLECTIONS = { auto: "cars", viaje: "places" };

function DirectChatBox() {
  const { otherUserId, type: routeType, itemId } = useParams();
  const chatType = ITEM_COLLECTIONS[routeType] && itemId ? routeType : "directo";
  const isItemChat = chatType !== "directo";
  const [item, setItem] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Estados para emojis
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [activeEmojiTab, setActiveEmojiTab] = useState("Frecuentes");
  
  // Estados para cámara
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  
  // Estados para adjuntos
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleBack = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    navigate(-1);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Obtener usuario actual
  useEffect(() => {
    const fetchAuthUser = async () => {
      const currentAuthUser = auth.currentUser;
      
      if (!currentAuthUser) {
        navigate("/login");
        return;
      }

      const userDocRef = doc(db, "users", currentAuthUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        setCurrentUser({
          uid: currentAuthUser.uid,
          ...userDoc.data(),
        });
      } else {
        setCurrentUser({
          uid: currentAuthUser.uid,
          displayName: currentAuthUser.displayName || "Usuario",
          profileImageUrl: null,
        });
      }
    };
    
    fetchAuthUser();
  }, [navigate]);

  // Obtener o crear conversación
  useEffect(() => {
    const fetchOrCreateConversation = async () => {
      if (!currentUser || !otherUserId) return;

      try {
        const otherUserRef = doc(db, "users", otherUserId);
        const otherUserDoc = await getDoc(otherUserRef);
        
        let otherUserData = null;
        if (otherUserDoc.exists()) {
          otherUserData = otherUserDoc.data();
          setOtherUser({
            uid: otherUserId,
            ...otherUserData,
          });
        } else {
          setError(<T>Usuario no encontrado</T>);
          setLoading(false);
          return;
        }

        const participantsSorted = [currentUser.uid, otherUserId].sort();

        if (isItemChat) {
          const itemSnap = await getDoc(doc(db, ITEM_COLLECTIONS[chatType], itemId));
          const itemData = itemSnap.exists() ? { id: itemSnap.id, ...itemSnap.data() } : null;
          setItem(itemData);

          // Puede existir con dos formatos de id (CarDetail usaba solo los
          // dos uids), así que se busca por tipo + publicación.
          const existing = await getDocs(query(
            collection(db, "conversations"),
            where("type", "==", chatType),
            where("itemId", "==", itemId),
            where("participants", "array-contains", currentUser.uid)
          ));
          const found = existing.docs.find((d) => d.data().participants.includes(otherUserId));
          if (found) {
            setConversationId(found.id);
            setLoading(false);
            return;
          }
          if ((otherUserData.blockedUsers || {})[currentUser.uid]) {
            setError(<T>No podés enviar mensajes a esta cuenta.</T>);
            setLoading(false);
            return;
          }
          const itemConversationId = [currentUser.uid, otherUserId, chatType, itemId].sort().join("_");
          await setDoc(doc(db, "conversations", itemConversationId), {
            type: chatType,
            itemId,
            itemOwnerId: itemData?.uid || null,
            participants: participantsSorted,
            createdAt: serverTimestamp(),
            lastMessageTime: serverTimestamp(),
            unreadCount: {
              [currentUser.uid]: 0,
              [otherUserId]: 0,
            },
          }, { merge: true });
          setConversationId(itemConversationId);
          setLoading(false);
          return;
        }

        const directConversationId = `directo_${participantsSorted[0]}_${participantsSorted[1]}`;

        const conversationRef = doc(db, "conversations", directConversationId);
        const conversationSnap = await getDoc(conversationRef);

        if (!conversationSnap.exists()) {
          // Preferencia "Quién puede enviarte mensajes" (ver ajustes de privacidad).
          // Solo aplica al iniciar una conversación nueva; el bloqueo (blockedUsers)
          // ya se valida también en firestore.rules para cada mensaje.
          if ((otherUserData.blockedUsers || {})[currentUser.uid]) {
            setError(<T>No podés enviar mensajes a esta cuenta.</T>);
            setLoading(false);
            return;
          }
          if (otherUserData.mensajesDe === 'nadie') {
            setError(<T>Esta cuenta no acepta mensajes.</T>);
            setLoading(false);
            return;
          }
          if (otherUserData.mensajesDe === 'seguidos') {
            const otherFollowingSnap = await getDoc(doc(db, "userFollowing", otherUserId));
            const otherFollowsMe = otherFollowingSnap.exists() && !!otherFollowingSnap.data()[currentUser.uid];
            if (!otherFollowsMe) {
              setError(<T>Esta cuenta no acepta mensajes de vos.</T>);
              setLoading(false);
              return;
            }
          }

          await setDoc(conversationRef, {
            type: "directo",
            participants: participantsSorted,
            createdAt: serverTimestamp(),
            lastMessageTime: serverTimestamp(),
            unreadCount: {
              [currentUser.uid]: 0,
              [otherUserId]: 0,
            },
          });
        }

        setConversationId(directConversationId);
        setLoading(false);
      } catch (error) {
        console.error("Error:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchOrCreateConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, otherUserId, chatType, itemId]);

  const markMessagesAsRead = useCallback(async (msgs) => {
    if (!currentUser || !conversationId) return;
    const batch = writeBatch(db);
    msgs.forEach((message) => {
      if (message.senderId !== currentUser.uid && !message.read) {
        const messageRef = doc(db, `conversations/${conversationId}/messages`, message.id);
        batch.update(messageRef, { read: true, readAt: serverTimestamp() });
      }
    });
    const conversationRef = doc(db, "conversations", conversationId);
    batch.update(conversationRef, { [`unreadCount.${currentUser.uid}`]: 0 });
    await batch.commit();
  }, [currentUser, conversationId]);

  // Escuchar mensajes
  useEffect(() => {
    if (!conversationId || !currentUser) return;

    const messagesRef = collection(db, "conversations", conversationId, "messages");
    const q = query(messagesRef, orderBy("timestamp"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(newMessages);
      scrollToBottom();
      markMessagesAsRead(newMessages);
    });

    return () => unsubscribe();
  }, [conversationId, currentUser, markMessagesAsRead]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [stream, previewUrl]);

  // ==================== EMOJIS ====================
  
  const handleEmojiClick = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setEmojiAnchor(null);
  };

  // ==================== CÁMARA ====================
  
  const handleOpenCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "user" } 
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error("Error accediendo a la cámara:", error);
      setError(<T>No se pudo acceder a la cámara</T>);
    }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setCapturedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      setStream(null);
      setShowCamera(false);
    }, 'image/jpeg', 0.95);
  };

  const handleCloseCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setStream(null);
    setShowCamera(false);
  };

  // ==================== ADJUNTOS ====================
  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError(<T>El archivo es demasiado grande (máx 10MB)</T>);
      return;
    }

    setSelectedFile(file);
    
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleCancelAttachment = () => {
    setSelectedFile(null);
    setCapturedImage(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ==================== ENVIAR MENSAJE ====================
  
  const uploadFile = async (file) => {
    const timestamp = Date.now();
    const fileName = `${conversationId}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, `chats/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return {
      url: downloadURL,
      name: file.name,
      type: file.type,
      size: file.size,
    };
  };

  const handleSendMessage = async () => {
    if (!currentUser || !conversationId || sending) return;
    
    const fileToSend = capturedImage || selectedFile;
    
    if (newMessage.trim() === "" && !fileToSend) {
      return;
    }

    setSending(true);
    setUploading(!!fileToSend);

    try {
      const safeSenderDisplayName =
        currentUser.displayName || currentUser.fullname || currentUser.email || "Usuario";

      const messageData = {
        content: newMessage.trim(),
        timestamp: serverTimestamp(),
        senderId: currentUser.uid,
        senderDisplayName: safeSenderDisplayName,
        senderProfileImageUrl: currentUser.profileImageUrl || null,
        read: false,
      };

      if (fileToSend) {
        const fileData = await uploadFile(fileToSend);
        messageData.attachment = fileData;
        messageData.type = fileToSend.type.startsWith('image/') ? 'image' : 'file';
      } else {
        messageData.type = 'text';
      }

      const messagesRef = collection(db, `conversations/${conversationId}/messages`);
      await addDoc(messagesRef, messageData);

      const conversationRef = doc(db, "conversations", conversationId);
      await updateDoc(conversationRef, {
        lastMessage: {
          content: messageData.type === 'text' ? newMessage.trim() : 
                   messageData.type === 'image' ? '📷 Imagen' : '📎 Archivo',
          senderId: currentUser.uid,
          timestamp: serverTimestamp(),
        },
        lastMessageTime: serverTimestamp(),
        [`unreadCount.${otherUserId}`]: increment(1),
      });

      try {
        const notificationMessage = messageData.type === 'text' 
          ? (newMessage.length > 50 ? newMessage.substring(0, 50) + '...' : newMessage)
          : messageData.type === 'image' 
            ? '📷 Envio una imagen'
            : '📎 Envio un archivo';

        // notifyUser respeta "Mensajes y pedidos" en los ajustes del receptor.
        await notifyUser({
          userId: otherUserId,
          companyId: currentUser.uid,
          category: "messages",
          type: "message",
          message: notificationMessage,
          postId: conversationId,
        });
      } catch (notifError) {
        console.error("⚠️ Error creando notificación (no crítico):", notifError);
      }

      setNewMessage("");
      handleCancelAttachment();
      scrollToBottom();
    } catch (error) {
      console.error("Error enviando mensaje:", error);
      // En los chats por publicación firestore.rules rechaza teléfonos y
      // emails (canSendChatMessage).
      setError(
        isItemChat && error.code === "permission-denied"
          ? <T>No se pueden enviar teléfonos ni emails por este chat. Usá el botón de WhatsApp de la publicación.</T>
          : <T>No se pudo enviar el mensaje. Intentá de nuevo.</T>
      );
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  // ==================== RENDERIZAR ====================

  // ==================== RENDERIZAR ====================
  
  const formatTime = (timestamp) => {
    if (timestamp) {
      const date = timestamp.toDate();
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return "";
  };

  const getMessageStatusText = (message, isCurrentUser) => {
    if (!isCurrentUser) return "";
    // Solo "Visto": la hora del mensaje ya está al lado y dos horas confunden.
    if (message.read) return "Visto";
    return "Enviado";
  };

  const renderMessage = (message) => {
    const isCurrentUser = message.senderId === currentUser?.uid;
    const statusText = getMessageStatusText(message, isCurrentUser);

    return (
      <Box
        key={message.id}
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: isCurrentUser ? "flex-end" : "flex-start",
          px: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isCurrentUser ? "row-reverse" : "row",
            alignItems: "flex-end",
            maxWidth: "75%",
            gap: 1,
          }}
        >
          <Avatar
            src={isCurrentUser ? currentUser?.profileImageUrl : otherUser?.profileImageUrl}
            sx={{ width: 32, height: 32 }}
          >
            <PersonIcon />
          </Avatar>

          <Box>
            <Box
              sx={{
                bgcolor: isCurrentUser ? brandColors.primary : brandColors.backgroundPaper,
                p: 1.5,
                borderRadius: 2,
                boxShadow: 1,
                border: isCurrentUser ? '1px solid rgba(255,255,255,0.15)' : `1px solid ${brandColors.divider}`,
                position: "relative",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  color: isCurrentUser ? "#90caf9" : brandColors.accent,
                  mb: 0.5,
                  display: "block",
                }}
              >
                {isCurrentUser ? <T>Tú</T> : (otherUser?.displayName || <T>Usuario</T>)}
              </Typography>

              {message.type === 'image' && message.attachment && (
                <Box
                  component="img"
                  src={safeHttpUrl(message.attachment.url) || undefined}
                  alt={<T>Imagen</T>}
                  sx={{
                    maxWidth: 250,
                    maxHeight: 250,
                    borderRadius: 1,
                    mb: message.content ? 1 : 0,
                    cursor: 'pointer',
                    display: 'block',
                  }}
                  onClick={() => openSafeUrl(message.attachment.url)}
                />
              )}

              {message.type === 'file' && message.attachment && (
                <Paper
                  sx={{
                    p: 1,
                    mb: message.content ? 1 : 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    bgcolor: 'action.hover',
                  }}
                  onClick={() => openSafeUrl(message.attachment.url)}
                >
                  <AttachFileIcon fontSize="small" />
                  <Typography variant="caption">
                    {message.attachment.name}
                  </Typography>
                </Paper>
              )}

              {message.content && (
                <Typography
                  variant="body1"
                  sx={{
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                    fontSize: "0.95rem",
                    color: isCurrentUser ? "#ffffff" : "text.primary",
                  }}
                >
                  {message.content}
                </Typography>
              )}

              <Typography
                variant="caption"
                sx={{
                  color: isCurrentUser ? "rgba(255,255,255,0.7)" : "text.secondary",
                  fontSize: "0.65rem",
                  display: "block",
                  textAlign: "right",
                  mt: 0.5,
                }}
              >
                {formatTime(message.timestamp)}
                {statusText && ` - `}<T>{statusText}</T>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: brandColors.backgroundDefault,
      }}
    >
      <AppBar position="fixed" sx={{ bgcolor: barColors.background }}>
        <Toolbar sx={{ justifyContent: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              maxWidth: { xs: "100%", md: `${DESKTOP_CONTENT_MAX_WIDTH}px` },
              mx: "auto",
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBack}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              src={otherUser?.profileImageUrl}
              sx={{ width: 40, height: 40, ml: 2, mr: 2 }}
            >
              <PersonIcon />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">{otherUser?.displayName || <T>Usuario</T>}</Typography>
              <Typography variant="caption" sx={{ color: barColors.textMuted }}>
                {chatType === "auto" ? <T>Consulta por auto</T> : chatType === "viaje" ? <T>Consulta por lugar</T> : <T>Chat directo</T>}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          mt: 8,
          mb: 1,
          pt: 2,
          width: "100%",
          maxWidth: { xs: "100%", md: `${DESKTOP_CONTENT_MAX_WIDTH}px` },
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          // Barra de scroll fina y casi invisible hasta pasar el mouse.
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(128,128,128,0.35) transparent",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(128,128,128,0.3)", borderRadius: 3 },
          "&:hover::-webkit-scrollbar-thumb": { backgroundColor: "rgba(128,128,128,0.55)" },
          "&::-webkit-scrollbar-button": { display: "none", height: 0 },
        }}
      >
        {isItemChat && item && (
          <Paper
            component={RouterLink}
            to={chatType === "auto" ? `/cars/${item.id}` : `/trips/${item.id}`}
            variant="outlined"
            sx={{
              mx: 2,
              mb: 2,
              p: 1.25,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              borderRadius: 3,
              textDecoration: "none",
              color: "inherit",
              bgcolor: brandColors.backgroundPaper,
            }}
          >
            {item.images?.[0] && (
              <Box
                component="img"
                src={item.images[0]}
                alt={item.title}
                sx={{ width: 56, height: 56, borderRadius: 2, objectFit: "cover", flexShrink: 0 }}
              />
            )}
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: 15 }}>{item.title}</Typography>
              <Typography sx={{ fontSize: 14, color: brandColors.accent, fontWeight: 700 }}>
                ${Number(item.price).toLocaleString()}
                {chatType === "viaje" && <Typography component="span" sx={{ fontSize: 12, color: "text.secondary", fontWeight: 400 }}> <T>por día</T></Typography>}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}><T>Ver publicación</T></Typography>
            </Box>
          </Paper>
        )}
        {messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Avatar
              src={otherUser?.profileImageUrl}
              sx={{ width: 80, height: 80 }}
            >
              <PersonIcon sx={{ fontSize: 50 }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary">
              <T>Comienza una conversación</T>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <T>con</T> {otherUser?.displayName}
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {previewUrl && (
        <Paper
          sx={{
            mx: "auto",
            mb: 1,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: brandColors.backgroundPaper,
            width: "100%",
            maxWidth: { xs: "calc(100% - 32px)", md: `${DESKTOP_CONTENT_MAX_WIDTH}px` },
          }}
        >
          {(capturedImage || (selectedFile && selectedFile.type.startsWith('image/'))) ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Preview"
              sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachFileIcon />
              <Typography variant="body2">{selectedFile?.name}</Typography>
            </Box>
          )}
          <IconButton onClick={handleCancelAttachment} size="small" sx={{ ml: 'auto' }}>
            <CloseIcon />
          </IconButton>
        </Paper>
      )}

      <Paper
        elevation={4}
        sx={{
          p: 0.8,
          mx: "auto",
          mb: 1,
          borderRadius: 3,
          bgcolor: brandColors.backgroundPaper,
          width: "100%",
          maxWidth: { xs: "calc(100% - 16px)", md: `${DESKTOP_CONTENT_MAX_WIDTH}px` },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
          <IconButton
            onClick={(e) => setEmojiAnchor(e.currentTarget)}
            size="small"
            sx={{ color: "text.secondary", p: 0.8 }}
          >
            <EmojiEmotionsIcon fontSize="small" />
          </IconButton>

          <TextField
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Escribe un mensaje..."
            variant="standard"
            fullWidth
            multiline
            maxRows={4}
            disabled={uploading}
            InputProps={{
              disableUnderline: true,
            }}
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "0.9rem",
                px: 0.5,
              },
            }}
          />

          <IconButton
            onClick={() => fileInputRef.current?.click()}
            size="small"
            sx={{ color: "text.secondary", p: 0.8 }}
          >
            <AttachFileIcon fontSize="small" sx={{ transform: 'rotate(45deg)' }} />
          </IconButton>

          <IconButton
            onClick={handleOpenCamera}
            size="small"
            sx={{ color: "text.secondary", p: 0.8 }}
          >
            <CameraAltIcon fontSize="small" />
          </IconButton>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/*,.pdf,.doc,.docx,.txt"
            onChange={handleFileSelect}
          />

          <IconButton
            onClick={handleSendMessage}
            disabled={(!newMessage.trim() && !selectedFile && !capturedImage) || sending}
            size="small"
            sx={{
              bgcolor: brandColors.action,
              color: brandColors.onAction,
              width: 36,
              height: 36,
              p: 0,
              "&:hover": {
                bgcolor: brandColors.actionHover,
              },
              "&:disabled": {
                bgcolor: "#E0E0E0",
                color: "#9E9E9E",
              },
            }}
          >
            {uploading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>
      </Paper>

      <Menu
        anchorEl={emojiAnchor}
        open={Boolean(emojiAnchor)}
        onClose={() => setEmojiAnchor(null)}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 320,
          },
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {Object.keys(EMOJI_CATEGORIES).map((category) => (
              <Button
                key={category}
                size="small"
                onClick={() => setActiveEmojiTab(category)}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  bgcolor: activeEmojiTab === category ? 'primary.light' : 'transparent',
                  color: activeEmojiTab === category ? 'primary.main' : 'text.secondary',
                }}
              >
                <T>{category}</T>
              </Button>
            ))}
          </Box>
        </Box>

        <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
          {EMOJI_CATEGORIES[activeEmojiTab].map((emoji, index) => (
            <MenuItem
              key={index}
              onClick={() => handleEmojiClick(emoji)}
              sx={{
                fontSize: 24,
                p: 0.5,
                minWidth: 'auto',
                justifyContent: 'center',
                borderRadius: 1,
              }}
            >
              {emoji}
            </MenuItem>
          ))}
        </Box>
      </Menu>

      <Dialog
        open={showCamera}
        onClose={handleCloseCamera}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <T>Tomar Foto</T>
          <IconButton
            onClick={handleCloseCamera}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', paddingTop: '75%' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 8,
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCamera}><T>Cancelar</T></Button>
          <Button onClick={handleCapturePhoto} variant="contained" startIcon={<CameraAltIcon />}>
            <T>Capturar</T>
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DirectChatBox;
